import { httpClient, DeviceDetector } from "@utils";
import { GameLaunchDto } from "@api-types/games";
import {
	CasinoRequestFilter,
	Games,
	MenuDataResponse,
} from "@data-types/games";
import { ApiRequestContextData, ContextOptions } from "@utils/api-config/types";
import { getApplicationRequestHeaders } from "@utils/api-config/getApplicationRequestHeader";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { getApiRequestContext } from "@utils/api-config";

export class CasinoApiService {
	static casinoBaseUrl(context: ApiRequestContextData | null) {
		return `${CasinoApiAddress}${context?.agencyKey}/casino`;
	}

	static getRequestHeaders = (context: ApiRequestContextData | null) => {
		const appHeaders = getApplicationRequestHeaders(context);
		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		return appHeaders;
	};

	static getApiRequestContext(
		options: ContextOptions = createApiRequestOptions(
			RequestContextType.Online
		)
	) {
		return getApiRequestContext(options);
	}

	static async getCasinoLobbyAsync(
		filter: CasinoRequestFilter
	): Promise<MenuDataResponse> {
		let url = `${this.casinoBaseUrl(this.getApiRequestContext())}/lobby${
			DeviceDetector.isMobileTheme ? "/mobile" : ""
		}`;

		const urlParams = new URLSearchParams();
		for (const [key, value] of Object.entries(filter)) {
			if (value == null) {
				continue;
			}

			urlParams.append(key, value);
		}

		return await httpClient.get(
			`${url}?${urlParams.toString()}`,
			this.getRequestHeaders(this.getApiRequestContext())
		);
	}

	static async getFeaturedGames(
		filter: CasinoRequestFilter
	): Promise<MenuDataResponse> {
		let url = `${this.casinoBaseUrl(this.getApiRequestContext())}/lobby${
			DeviceDetector.isMobileTheme ? "/mobile" : ""
		}/featured-games`;

		const urlParams = new URLSearchParams();
		for (const [key, value] of Object.entries(filter)) {
			if (value == null) {
				continue;
			}

			urlParams.append(key, value);
		}

		return await httpClient.get(
			`${url}?${urlParams.toString()}`,
			this.getRequestHeaders(this.getApiRequestContext())
		);
	}

	static async lobbyLaunch(providerAbrv: string) {
		const url = `${this.casinoBaseUrl(this.getApiRequestContext())}/${providerAbrv}/launch${
			DeviceDetector.isMobileTheme ? "/mobile" : ""
		}/lobby`;

		return await httpClient.get(
			url,
			this.getRequestHeaders(this.getApiRequestContext())
		);
	}

	static async pingGameSessionToken(
		providerAbrv: string,
		sessionToken: string
	) {
		const url = `${this.casinoBaseUrl(this.getApiRequestContext())}/${providerAbrv}/launch/session-token/${sessionToken}`;

		return await httpClient.get(
			url,
			this.getRequestHeaders(this.getApiRequestContext())
		);
	}

	static async getGame(gameId: string): Promise<MenuDataResponse> {
		const url = `${this.casinoBaseUrl(this.getApiRequestContext())}/lobby${
			DeviceDetector.isMobileTheme ? "/mobile" : ""
		}/games/${gameId}`;

		return await httpClient.get(
			url,
			this.getRequestHeaders(this.getApiRequestContext())
		);
	}

	static async launchGame(
		casinoProviderAbrv: string,
		gameId: string
	): Promise<GameLaunchDto> {
		return await httpClient.get(
			`${this.casinoBaseUrl(this.getApiRequestContext())}/${casinoProviderAbrv}/launch${
				DeviceDetector.isMobileTheme ? "/mobile" : ""
			}/${gameId}`,
			this.getRequestHeaders(this.getApiRequestContext())
		);
	}

	static async launchGameWithPlayType(
		casinoProviderAbrv: string,
		gameId: string,
		isPlayForFun: boolean = true
	): Promise<GameLaunchDto> {
		return await httpClient.get(
			`${this.casinoBaseUrl(this.getApiRequestContext())}/${casinoProviderAbrv}/launch${
				DeviceDetector.isMobileTheme ? "/mobile" : ""
			}/${gameId}/${isPlayForFun}`,
			this.getRequestHeaders(this.getApiRequestContext())
		);
	}

	static async launchMatrixGame(
		gameId: string,
		playType: string | null
	): Promise<GameLaunchDto> {
		const device = DeviceDetector.isMobileTheme ? "/mobile" : "";
		const gameMode = playType === "for-fun" ? "/for-fun" : "";

		const url = `${this.casinoBaseUrl(this.getApiRequestContext())}/everymatrix/launch${device}${gameMode}/${gameId}`;

		const headers = this.getRequestHeaders(this.getApiRequestContext());

		return await httpClient.get(url, headers);
	}

	static async getGames(filter: CasinoRequestFilter): Promise<Games> {
		const url = `${this.casinoBaseUrl(this.getApiRequestContext())}/lobby${
			DeviceDetector.isMobileTheme ? "/mobile" : ""
		}/games`;

		const urlParams = new URLSearchParams();
		for (const [key, value] of Object.entries(filter)) {
			if (value == null) {
				continue;
			}

			urlParams.append(key, value);
		}

		return await httpClient.get(
			`${url}?${urlParams.toString()}`,
			this.getRequestHeaders(this.getApiRequestContext())
		);
	}
}
